import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Plus } from 'lucide-react';
import {
  useToast,
  PageHeader, Button, Input, EmptyState,
} from '../components/ui';

const OutletManagement = () => {
  const { toast } = useToast();
  const [outlets, setOutlets] = useState([]);
  const [form, setForm] = useState({ name: '', address: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchOutlets(); }, []);

  const fetchOutlets = async () => {
    try {
      const res = await axios.get('/api/outlets', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setOutlets(res.data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post('/api/outlets', form, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setForm({ name: '', address: '', phone: '', email: '' });
      fetchOutlets();
      toast.success('Outlet created successfully');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error creating outlet');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Outlets"
        subtitle="Manage your restaurant locations and per-outlet settings."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Outlets table */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-neutral-800">All outlets</h2>
            <span className="text-xs text-neutral-500">
              {outlets.length} location{outlets.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="app-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {outlets.length === 0 && (
                  <tr>
                    <td colSpan="4">
                      <EmptyState
                        icon={Building2}
                        title="No outlets yet"
                        message="Create your first outlet using the form on the right."
                      />
                    </td>
                  </tr>
                )}
                {outlets.map(outlet => (
                  <tr key={outlet._id}>
                    <td className="font-medium">{outlet.name}</td>
                    <td className="text-neutral-600">
                      {outlet.address || <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="text-neutral-600">
                      {outlet.phone || <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="text-neutral-600">
                      {outlet.email || <span className="text-neutral-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create form */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden h-fit">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2">
            <Plus size={16} className="text-primary-600" />
            <h2 className="text-sm font-semibold text-neutral-800">Create outlet</h2>
          </div>
          <form onSubmit={handleSubmit} className="p-5 space-y-3">
            <Input
              label="Outlet name"
              type="text"
              placeholder="e.g. Downtown Branch"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Input
              label="Address"
              type="text"
              placeholder="Street, city"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              required
            />
            <Input
              label="Phone (optional)"
              type="text"
              placeholder="+92-..."
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <Input
              label="Email (optional)"
              type="email"
              placeholder="outlet@restaurant.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <div className="pt-1">
              <Button type="submit" variant="primary" fullWidth loading={submitting}>
                Create outlet
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default OutletManagement;
